import { EndpointPermissionRule } from '../core/permissions/endpoint-permission.models';
import { P, YONETIM_FEATURE_PERMISSIONS } from './portal-permissions.constants';

/** Herhangi bir Yönetim alt modülü read → ortak form/lookup API */
const YONETIM_ALL = [...YONETIM_FEATURE_PERMISSIONS];

function multiRead(
  pattern: string,
  permissionKeys: string[],
  methods: ('GET' | 'POST')[] = ['GET', 'POST'],
): EndpointPermissionRule[] {
  return methods.map((method) => ({
    method,
    pattern,
    permissionKeys,
    access: 'read' as const,
  }));
}

function multiWrite(
  pattern: string,
  permissionKeys: string[],
  methods: ('GET' | 'POST')[] = ['POST'],
): EndpointPermissionRule[] {
  return methods.map((method) => ({
    method,
    pattern,
    permissionKeys,
    access: 'write' as const,
  }));
}

/** Lokasyon kademeli API (Info / Status / Calculation) — Yonetim.Lokasyonlar write */
const LOKASYON_WRITE = [P.Yonetim.Lokasyonlar];

/**
 * Yönetim ekranları arası paylaşılan lookup endpoint'leri.
 * Örn: Alt Şirketler read + Şirketim kapalı → GetCompany yine çalışır.
 * Kullanıcılar read → GetAllRoles dropdown çalışır (Roller read şart değil).
 */
export const YONETIM_ENDPOINT_EXTRA_RULES: readonly EndpointPermissionRule[] =
  [
    ...multiRead('api/Company/GetCompany', YONETIM_ALL),
    ...multiRead('api/Company/GetCompanyDetailForApplication', YONETIM_ALL, [
      'GET',
    ]),
    ...multiRead('api/Company/GetCountries', YONETIM_ALL, ['GET']),
    ...multiRead('api/Company/GetCities', YONETIM_ALL),
    ...multiRead('api/Company/GetSegments', YONETIM_ALL, ['GET']),
    ...multiRead('api/Company/GetNaceCodes', YONETIM_ALL, ['GET']),
    ...multiRead(
      'api/Company/GetCountriesFromEnergyEfficiency',
      YONETIM_ALL,
      ['GET'],
    ),
    // EmisyonFactor V2 elektrik sayfasi da bu lookup'i kullanir (bkz. GetAircraft notu).
    ...multiRead('api/Company/GetElectricityConnectionTypes', [
      ...YONETIM_ALL,
      P.EmisyonFaktorleri.Kaynak,
    ]),
    ...multiRead('api/Company/GetEnergyEfficiencyClasses', YONETIM_ALL, [
      'GET',
    ]),
    // EmisyonFactor V2 sivil havacilik onizlemesi de bu lookup'i kullanir. Ayni pattern'de
    // ilk extra kural kazandigi icin Kaynak key'i BURADA eklenmeli (emisyon dosyasinda degil).
    ...multiRead('api/Company/GetAircraft', [
      ...YONETIM_ALL,
      P.EmisyonFaktorleri.Kaynak,
    ]),
    ...multiRead('api/Company/GetCarbonGateCatalog', [
      ...YONETIM_ALL,
      P.Tedarikci.Module,
    ]),
    ...multiRead('api/Company/GetEmissionDeviceTypes', YONETIM_ALL),
    // EmisyonFactor V2 hareketli yanma sayfasi da bu lookup'i kullanir (bkz. GetAircraft notu).
    ...multiRead('api/Company/GetVehicleTypes', [
      ...YONETIM_ALL,
      P.EmisyonFaktorleri.Kaynak,
    ]),
    ...multiRead('api/Company/GetIcaoRouteGroups', YONETIM_ALL),

    ...multiRead('api/Company/GetCompanies', [
      P.Yonetim.Kullanicilar,
      P.Yonetim.Sirketim,
      P.Yonetim.AltSirketler,
      P.UygulamaYonetimi.Sirketler,
    ]),

    /** Kullanıcı oluştur/düzenle modalındaki uygulama dropdown */
    ...multiRead(
      'api/Application/GetAllApplication',
      [P.Yonetim.Kullanicilar, P.UygulamaYonetimi.Sirketler],
      ['GET'],
    ),

    ...multiRead('api/Role/GetAllRoles', [
      P.Yonetim.Kullanicilar,
      P.Yonetim.Roller,
    ]),

    /** Sorumlu kişi dropdown — Lokasyonlar / Alt Şirketler / Şirketim formları */
    ...multiRead('api/Identity/GetAllUsers', [
      P.Yonetim.Kullanicilar,
      P.Yonetim.Lokasyonlar,
      P.Yonetim.AltSirketler,
      P.Yonetim.Sirketim,
    ]),

    /**
     * Lokasyon kademeli API (admin/locations).
     * Kademe 1 = Info, Kademe 2 = Status, Kademe 3 = Calculation.
     * Eski CreateLocation/UpdateLocation hâlâ Alt Şirketler için kullanılıyor.
     */
    ...multiWrite('api/Company/CreateLocationInfo', LOKASYON_WRITE),
    ...multiWrite('api/Company/CreateLocationInfoWithLogo', LOKASYON_WRITE),
    ...multiWrite('api/Company/UpdateLocationInfo', LOKASYON_WRITE),
    ...multiWrite('api/Company/UpdateLocationInfoWithLogo', LOKASYON_WRITE),
    ...multiWrite('api/Company/SetLocationStatus', LOKASYON_WRITE),
    ...multiWrite('api/Company/SaveLocationCalculationData', LOKASYON_WRITE),
  ];
